import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, ChevronLeft, X, Check, Loader2, Newspaper, TrendingUp, ShoppingBag,
  Clock, Sparkles, Lock, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api';
import TrendCard from '@/components/TrendCard';

/**
 * MyPortfolioV2 — search-chip portfolio UX (Iter 61d).
 *
 * Per user spec:
 *   - 3 frames (News, Investor, Reseller)
 *   - In each frame, list categories
 *   - User picks ONE category → empty search bar opens with italic placeholder
 *   - User types entity, hits Enter / comma → chip appears (max 10)
 *   - Empty portfolio is acceptable (no minimum)
 *   - On save: matching items appear immediately; missing items show
 *     "currently unavailable, will be added within 24h" status.
 */

const FRAMES = [
  // May 2026 — match Dashboard tab colours exactly: News=Blue, Investor=Yellow, Reseller=Cyan
  { key: 'news',     label: 'News',     Icon: Newspaper,    accent: '#3B82F6',
    placeholder: 'Please type the topics you wish to follow' },
  { key: 'investor', label: 'Investor', Icon: TrendingUp,   accent: '#FACC15',
    placeholder: 'Please type the company, brand or model you wish to follow' },
  { key: 'reseller', label: 'Marketplace seller', Icon: ShoppingBag,  accent: '#06B6D4',
    placeholder: 'Please type the company, brand or model you wish to follow' },
];

const CATEGORIES = {
  news: [
    { key: 'world_news', label: 'World' }, { key: 'politics', label: 'Politics' },
    { key: 'technology', label: 'Tech' }, { key: 'finance', label: 'Finance' },
    { key: 'ai', label: 'AI' }, { key: 'sports', label: 'Sports' },
    { key: 'entertainment', label: 'Entertainment' }, { key: 'internet_culture', label: 'Internet' },
    { key: 'economy', label: 'Economy' }, { key: 'lifestyle', label: 'Lifestyle' },
  ],
  investor: [
    { key: 'crypto', label: 'Crypto' }, { key: 'stocks', label: 'Stocks' },
    { key: 'commodities', label: 'Commodities' }, { key: 'precious_metals', label: 'Precious Metals' },
    { key: 'forex', label: 'Forex' }, { key: 'fantasy_sports', label: 'Fantasy Sports' },
    { key: 'sports_betting', label: 'Betting' },
  ],
  reseller: [
    { key: 'sneakers', label: 'Sneakers' }, { key: 'ecommerce', label: 'E-Commerce' },
    { key: 'fashion', label: 'Fashion' }, { key: 'electronics', label: 'Electronics' },
    { key: 'beauty', label: 'Beauty' }, { key: 'home', label: 'Home' },
    { key: 'watches', label: 'Watches' }, { key: 'wellness', label: 'Wellness' },
    { key: 'books', label: 'Books' }, { key: 'tickets', label: 'Tickets' },
    { key: 'spirits', label: 'Spirits' }, { key: 'baby_kids', label: 'Baby & Kids' },
    { key: 'auto_parts', label: 'Auto Parts' }, { key: 'tools_diy', label: 'Tools / DIY' },
    { key: 'gaming', label: 'Gaming' }, { key: 'trading_cards', label: 'Cards' },
    { key: 'toys', label: 'Toys' }, { key: 'collectibles', label: 'Collectibles' },
    { key: 'food', label: 'Food' },
  ],
};

const MAX_PER_CATEGORY = 10;

export default function MyPortfolioV2({ user, onBack, onTopicClick }) {
  const [loading, setLoading] = useState(true);
  const [tracked, setTracked] = useState({ news: {}, investor: {}, reseller: {} });
  const [activeFrame, setActiveFrame] = useState('news');
  const [openCategory, setOpenCategory] = useState({ news: null, investor: null, reseller: null });
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState(null);   // {news:{cat:{name:"available"|"pending"}}}
  const [feed, setFeed] = useState(null);
  const [feedLoading, setFeedLoading] = useState(false);
  // 'view' = mini-dashboard with TrendCards, 'edit' = search-chip setup UI.
  // Decided after the initial load based on whether user has any tracked entries.
  const [mode, setMode] = useState('edit');

  const isPremium = user?.subscription_status === 'active' || user?.subscription_status === 'trial';

  const totalChips = useMemo(() => {
    let n = 0;
    for (const fr of ['news', 'investor', 'reseller']) {
      for (const cat of Object.keys(tracked[fr] || {})) {
        n += (tracked[fr][cat] || []).length;
      }
    }
    return n;
  }, [tracked]);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    (async () => {
      try {
        const [t, f] = await Promise.all([
          api.getTrackedV2(),
          api.getTrackedV2Feed().catch(() => null),
        ]);
        const trackedDoc = t.tracked_v2 || { news: {}, investor: {}, reseller: {} };
        setTracked(trackedDoc);
        setFeed(f?.feed || null);
        // If the user already has tracked entries, open in view mode (mini-dashboard).
        // Empty portfolio → drop them straight into edit mode.
        const anyTracked = ['news', 'investor', 'reseller'].some(
          fr => Object.values(trackedDoc[fr] || {}).some(arr => (arr || []).length > 0)
        );
        setMode(anyTracked ? 'view' : 'edit');
      } catch (e) {
        toast.error('Failed to load portfolio');
      } finally {
        setLoading(false);
      }
    })();
  }, [isPremium]);

  if (loading) {
    return (
      <div data-testid="myportfolio-loading" className="min-h-screen flex items-center justify-center">
        <Loader2 className="text-emerald-500 animate-spin" size={28} />
      </div>
    );
  }
  if (!isPremium) {
    return <PremiumGate onBack={onBack} />;
  }

  const frame = FRAMES.find(f => f.key === activeFrame);
  const cats = CATEGORIES[activeFrame];
  const openCat = openCategory[activeFrame];

  const addChip = () => {
    const v = inputValue.trim();
    if (!v || !openCat) return;
    const current = tracked[activeFrame]?.[openCat] || [];
    if (current.length >= MAX_PER_CATEGORY) {
      toast.error(`Max ${MAX_PER_CATEGORY} per category`);
      return;
    }
    if (current.some(c => c.toLowerCase() === v.toLowerCase())) {
      toast.error('Already added');
      setInputValue('');
      return;
    }
    setTracked({
      ...tracked,
      [activeFrame]: { ...(tracked[activeFrame] || {}), [openCat]: [...current, v] },
    });
    setInputValue('');
  };

  const removeChip = (frameKey, catKey, name) => {
    const current = tracked[frameKey]?.[catKey] || [];
    const next = current.filter(c => c !== name);
    const nextFrame = { ...(tracked[frameKey] || {}) };
    if (next.length) nextFrame[catKey] = next;
    else delete nextFrame[catKey];
    setTracked({ ...tracked, [frameKey]: nextFrame });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.saveTrackedV2(tracked);
      setStatuses(res.statuses);
      toast.success(
        res.summary.pending > 0
          ? `${res.summary.available} tracking · ${res.summary.pending} queued for next refresh`
          : totalChips === 0 ? 'Empty portfolio saved' : `Tracking ${res.summary.available}`
      );
      // Refresh the live feed
      setFeedLoading(true);
      try {
        const f = await api.getTrackedV2Feed();
        setFeed(f.feed || null);
      } catch {
        setFeed(null);
      } finally {
        setFeedLoading(false);
      }
      // Switch to view mode (mini-dashboard) if user actually saved something.
      if (totalChips > 0) setMode('view');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="myportfolio-page" className="min-h-screen pb-32" style={{ background: '#0A0A0A' }}>
      {/* Header */}
      <div className="sticky top-0 z-30 px-4 py-3 backdrop-blur-md flex items-center justify-between"
        style={{ background: 'rgba(10,10,10,0.92)', borderBottom: '1px solid #1F2937' }}>
        <button data-testid="myportfolio-back" onClick={onBack} className="text-gray-400 hover:text-white p-1.5 -ml-1.5">
          <ChevronLeft size={22} />
        </button>
        <div className="flex items-center gap-1.5">
          <Briefcase size={14} className="text-emerald-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-white">My Portfolio</span>
        </div>
        <div style={{ width: 22 }} />
      </div>

      {/* View mode — mini-dashboard of saved entities. Iter 61e. */}
      {mode === 'view' && (
        <ViewModeDashboard
          feed={feed}
          loading={feedLoading}
          tracked={tracked}
          onEditAdd={() => setMode('edit')}
          onTopicClick={onTopicClick}
        />
      )}

      {/* Edit mode — search-chip setup UI */}
      {mode === 'edit' && (
      <div className="px-4 py-5 max-w-md mx-auto">
        <h1 className="text-3xl font-black text-white mb-2 tracking-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Track what matters to you
        </h1>
        <p className="text-[15px] text-white/85 font-medium mb-5 leading-relaxed"
           style={{ fontFamily: 'Manrope, sans-serif' }}>
          Pick a category in any frame, then type the topics, brands, or companies you want to follow.
          An empty portfolio is fine — you can add things any time.
        </p>

        {/* Frame tabs — bold, coloured outlines (matches Dashboard, May 2026) */}
        <div data-testid="frame-tabs" className="flex gap-2 mb-5">
          {FRAMES.map(f => {
            const isActive = activeFrame === f.key;
            return (
              <button
                key={f.key}
                data-testid={`frame-tab-${f.key}`}
                onClick={() => setActiveFrame(f.key)}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl transition-all"
                style={{
                  background: isActive ? `${f.accent}25` : 'rgba(255,255,255,0.03)',
                  border: `2px solid ${isActive ? f.accent : f.accent + '60'}`,
                  boxShadow: isActive ? `0 0 0 1px ${f.accent}30 inset` : 'none',
                }}
              >
                <f.Icon size={20} style={{ color: f.accent }} strokeWidth={2.5} />
                <span
                  className="text-base font-black uppercase tracking-tight"
                  style={{ color: isActive ? '#fff' : f.accent, fontFamily: 'Manrope, sans-serif' }}
                >
                  {f.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Categories within frame */}
        <div className="mb-3">
          <h2 className="text-[12px] font-black uppercase tracking-wider text-white mb-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
            Categories
          </h2>
          <div className="flex flex-wrap gap-2">
            {cats.map(c => {
              const trackedCount = (tracked[activeFrame]?.[c.key] || []).length;
              const isOpen = openCat === c.key;
              return (
                <button
                  key={c.key}
                  data-testid={`category-${activeFrame}-${c.key}`}
                  onClick={() => {
                    setOpenCategory({ ...openCategory, [activeFrame]: isOpen ? null : c.key });
                    setInputValue('');
                  }}
                  className="px-3.5 py-1.5 rounded-full text-[13px] font-black uppercase tracking-wide transition-all border flex items-center gap-1.5"
                  style={{
                    background: isOpen ? frame.accent : trackedCount > 0 ? `${frame.accent}25` : '#1A1A1A',
                    color: isOpen ? '#000' : trackedCount > 0 ? frame.accent : '#E5E7EB',
                    borderColor: isOpen ? frame.accent : trackedCount > 0 ? `${frame.accent}80` : 'rgba(255,255,255,0.22)',
                    borderWidth: '1.5px',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {c.label}
                  {trackedCount > 0 && (
                    <span className="text-[10px] font-black px-1 rounded"
                      style={{ background: isOpen ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.1)' }}>
                      {trackedCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search input + chips for the open category */}
        <AnimatePresence>
          {openCat && (
            <motion.div
              data-testid={`search-row-${activeFrame}-${openCat}`}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="rounded-2xl p-3 mb-4"
                style={{ background: '#111', border: `1px solid ${frame.accent}30` }}>
                <div className="text-[10px] uppercase tracking-wider font-bold mb-2"
                  style={{ color: frame.accent }}>
                  {(CATEGORIES[activeFrame].find(c => c.key === openCat) || {}).label}
                </div>
                {/* Chips */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(tracked[activeFrame]?.[openCat] || []).map(name => {
                    const status = statuses?.[activeFrame]?.[openCat]?.[name];
                    return (
                      <span
                        key={name}
                        data-testid={`chip-${activeFrame}-${openCat}-${name}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                        style={{
                          background: status === 'pending' ? 'rgba(245,158,11,0.15)'
                                     : status === 'available' ? 'rgba(16,185,129,0.15)'
                                     : `${frame.accent}25`,
                          color: status === 'pending' ? '#F59E0B'
                                : status === 'available' ? '#10B981'
                                : frame.accent,
                          border: `1px solid ${status === 'pending' ? '#F59E0B40'
                                  : status === 'available' ? '#10B98140'
                                  : frame.accent + '40'}`,
                        }}
                      >
                        {status === 'pending' && <Clock size={10} />}
                        {status === 'available' && <Check size={10} />}
                        {name}
                        <button
                          onClick={() => removeChip(activeFrame, openCat, name)}
                          className="ml-0.5 opacity-60 hover:opacity-100"
                          data-testid={`chip-remove-${name}`}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
                {/* Search input */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    data-testid={`search-input-${activeFrame}-${openCat}`}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addChip();
                      }
                    }}
                    placeholder={frame.placeholder}
                    className="flex-1 bg-black/40 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 outline-none"
                    style={{ fontStyle: 'italic', border: '1px solid rgba(255,255,255,0.08)' }}
                  />
                  <button
                    onClick={addChip}
                    disabled={!inputValue.trim()}
                    data-testid={`add-chip-${activeFrame}-${openCat}`}
                    className="px-3 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
                    style={{ background: frame.accent, color: '#000' }}
                  >
                    Add
                  </button>
                </div>
                <p className="text-[10px] text-gray-500 mt-1.5">
                  {(tracked[activeFrame]?.[openCat] || []).length}/{MAX_PER_CATEGORY} added · press Enter to add
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Save button — only appears once the user has added at least one entry
            (empty portfolio is still acceptable: just hit back to exit). */}
        {totalChips > 0 && (
          <button
            data-testid="myportfolio-save"
            onClick={save}
            disabled={saving}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-50 mb-5"
            style={{ background: '#10B981', color: '#000' }}
          >
            {saving ? 'Saving…' : `Save · ${totalChips} entit${totalChips === 1 ? 'y' : 'ies'}`}
          </button>
        )}

        {/* Pending notice — sticks once user has saved with pending items */}
        {statuses && (() => {
          const pending = [];
          for (const fr of ['news', 'investor', 'reseller']) {
            for (const cat of Object.keys(statuses[fr] || {})) {
              for (const [n, s] of Object.entries(statuses[fr][cat] || {})) {
                if (s === 'pending') pending.push({ frame: fr, cat, name: n });
              }
            }
          }
          if (!pending.length) return null;
          return (
            <div data-testid="pending-notice" className="rounded-2xl p-3 mb-5 flex gap-2.5"
              style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
              <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-200 leading-relaxed">
                <span className="font-bold">{pending.length} {pending.length === 1 ? 'item is' : 'items are'} currently unavailable.</span>
                {' '}We'll add {pending.length === 1 ? 'it' : 'them'} to our next update — should be available within 24 hours.
              </div>
            </div>
          );
        })()}

        {/* Live results — visible inline in edit mode after save */}
        <ResultsPanel feed={feed} loading={feedLoading} onTopicClick={onTopicClick} />
      </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// View-mode mini-dashboard — renders saved tracked items as TrendCards
// (same card format as the main dashboard, per user spec iter 61e).
// ─────────────────────────────────────────────────────────────────────

function ViewModeDashboard({ feed, loading, tracked, onEditAdd, onTopicClick }) {
  if (loading) {
    return (
      <div className="px-4 py-12 text-center">
        <Loader2 className="inline animate-spin text-emerald-500" size={22} />
      </div>
    );
  }

  // Build adapted entity arrays per frame for TrendCard
  const newsCards = (feed?.news || []).map((t, i) => ({
    canonical_id: t.id || `news-${i}`,
    entity_name: t.title || 'Untitled',
    category: t.category || 'news',
    hotness_score: t.trend_score || 50,
    trend_level: (t.trend_score || 50) >= 85 ? 'on_fire'
      : (t.trend_score || 50) >= 70 ? 'heating_up'
      : (t.trend_score || 50) >= 50 ? 'warming' : 'watching',
    direction_consensus: 'neutral',
    headline: t.headline || t.matched_term ? `Matches "${t.matched_term}"` : '',
    card_hook: t.card_hook,
    image_url: t.image_url || '',
    buy_sell: {},
    source_url: t.source_url || null,
    why_trending: t.matched_category ? `In ${t.matched_category}` : '',
    score_breakdown: {},
    source_count: t.source_count || 1,
    matched_term: t.matched_term || '',
  }));

  const investorCards = feed?.investor || [];
  const resellerCards = (feed?.reseller || []).map((p, i) => ({
    canonical_id: p.id || `prod-${i}`,
    entity_name: p.product || 'Product',
    category: p.category || 'reseller',
    hotness_score: p.sell_score || 50,
    trend_level: (p.sell_score || 50) >= 85 ? 'on_fire'
      : (p.sell_score || 50) >= 70 ? 'heating_up'
      : (p.sell_score || 50) >= 50 ? 'warming' : 'watching',
    direction_consensus: 'neutral',
    headline: p.matched_term ? `Matches "${p.matched_term}"` : '',
    buy_sell: { reseller: { action: p.decision === 'TEST FAST' ? 'soft_buy' : 'hold' } },
    why_trending: p.matched_category || '',
    score_breakdown: p.signal_breakdown || {},
    insight_reseller: p.card_text?.ai_insight,
    matched_term: p.matched_term || '',
  }));

  const totalCards = newsCards.length + investorCards.length + resellerCards.length;

  // ─── Unmatched tracked entities → placeholder cards ───
  // A tracked entity is "unmatched" when it hasn't surfaced in its frame's
  // feed yet (pipeline hasn't caught it). Render it as a placeholder card
  // with title + "Available within 24 hours" message (per user spec May 2026).
  const matchedTermsByFrame = {
    news:     new Set(newsCards.map(c => (c.matched_term || '').toLowerCase()).filter(Boolean)),
    investor: new Set(investorCards.map(c => (c.matched_term || '').toLowerCase()).filter(Boolean)),
    reseller: new Set(resellerCards.map(c => (c.matched_term || '').toLowerCase()).filter(Boolean)),
  };
  const unmatchedByFrame = { news: [], investor: [], reseller: [] };
  for (const fr of ['news', 'investor', 'reseller']) {
    for (const cat of Object.keys(tracked?.[fr] || {})) {
      for (const name of (tracked[fr][cat] || [])) {
        if (!matchedTermsByFrame[fr].has(name.toLowerCase())) {
          unmatchedByFrame[fr].push({ name, category: cat });
        }
      }
    }
  }

  // Tracked summary line — show what the user is following (e.g. "Tracking: Trump, Apple, Bitcoin")
  const allEntities = [];
  for (const fr of ['news', 'investor', 'reseller']) {
    for (const cat of Object.keys(tracked?.[fr] || {})) {
      for (const n of (tracked[fr][cat] || [])) allEntities.push(n);
    }
  }
  const summaryLine = allEntities.slice(0, 6).join(' · ') + (allEntities.length > 6 ? ` · +${allEntities.length - 6}` : '');

  return (
    <div data-testid="myportfolio-view-mode" className="px-4 py-5 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <h1 className="text-xl font-bold text-white">Your portfolio</h1>
        <button
          data-testid="myportfolio-edit-toggle"
          onClick={onEditAdd}
          className="text-[11px] font-semibold text-emerald-500 hover:text-emerald-400 px-2.5 py-1 rounded-full bg-emerald-500/10 inline-flex items-center gap-1"
        >
          <Pencil size={11} /> Edit
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-1 leading-snug">
        Tracking {allEntities.length} item{allEntities.length === 1 ? '' : 's'} · {totalCards} live card{totalCards === 1 ? '' : 's'}
      </p>
      {summaryLine && (
        <p className="text-[11px] text-emerald-500/80 mb-4 leading-snug truncate">{summaryLine}</p>
      )}

      {/* Section order per user spec (May 2026): Investor first, Reseller next,
          News last. All three always render (including empty ones) so the user
          sees the full portfolio structure. */}
      <ViewSection title="Investor" Icon={TrendingUp} accent="#10B981" cards={investorCards}
        unmatched={unmatchedByFrame.investor}
        persona="investor" tabContext="investor" />
      <ViewSection title="Marketplace seller" Icon={ShoppingBag} accent="#F59E0B" cards={resellerCards}
        unmatched={unmatchedByFrame.reseller}
        persona="reseller" tabContext="reseller" />
      <ViewSection title="News" Icon={Newspaper} accent="#3B82F6" cards={newsCards}
        unmatched={unmatchedByFrame.news}
        persona="investor" tabContext="news" isNews onTopicClick={onTopicClick} />
    </div>
  );
}


function ViewSection({ title, Icon, accent, cards, unmatched = [], persona, tabContext, isNews, onTopicClick }) {
  // Group matched cards by matched_term so each tracked topic gets its own row.
  const grouped = {};
  const order = [];
  for (const c of cards) {
    const key = c.matched_term || 'Tracked';
    if (!grouped[key]) { grouped[key] = []; order.push(key); }
    grouped[key].push(c);
  }

  const hasAnything = cards.length > 0 || unmatched.length > 0;

  return (
    <div className="mb-8" data-testid={`view-section-${tabContext}`}>
      <div className="flex items-center gap-2.5 mb-4 pb-2" style={{ borderBottom: `1px solid ${accent}25` }}>
        <Icon size={24} style={{ color: accent }} strokeWidth={2.5} />
        <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight" style={{ color: accent, fontFamily: 'Manrope, sans-serif' }}>
          {title}
        </h2>
        <span className="text-xs sm:text-sm text-gray-500 font-medium ml-1">
          · {hasAnything
              ? `${cards.length} live${unmatched.length ? ` · ${unmatched.length} pending` : ''}`
              : 'no tracked items'}
        </span>
      </div>
      {!hasAnything ? (
        <div
          className="rounded-xl p-3 text-center text-[11px] text-gray-500"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.08)' }}
        >
          No tracked items in this frame — add some from the Edit view.
        </div>
      ) : (
        <>
          {order.map(term => (
            <TopicRow
              key={term}
              term={term}
              cards={grouped[term].slice(0, 3)}
              persona={persona}
              tabContext={tabContext}
              isNews={isNews}
              onTopicClick={onTopicClick}
            />
          ))}
          {/* Pending (user-tracked but not yet in data) — placeholder cards
              per user spec May 2026: "show as empty cards in my portfolio with
              the title and 'Available within 24 hours' message". */}
          {unmatched.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2 px-0.5">
                <Clock size={12} style={{ color: '#9CA3AF' }} />
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Pending — available within 24 hours
                </span>
              </div>
              <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {unmatched.map(u => (
                  <PendingPlaceholderCard
                    key={`${tabContext}-pending-${u.name}`}
                    name={u.name}
                    category={u.category}
                    accent={accent}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/**
 * Placeholder card shown for tracked entities the pipeline hasn't caught yet.
 * Same rough card dimensions as TrendCard (so the grid stays aligned), but
 * with a skeletal look and a clear "Available within 24 hours" message.
 * Rendered by ViewSection below the live stories in My Portfolio.
 */
function PendingPlaceholderCard({ name, category, accent }) {
  return (
    <div
      data-testid={`portfolio-pending-${name.toLowerCase().replace(/\s+/g, '-')}`}
      className="relative rounded-xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(26,26,26,0.5) 0%, rgba(10,10,10,0.5) 100%)',
        border: '1px dashed rgba(255,255,255,0.10)',
        minHeight: 172,
      }}
    >
      {/* Accent strip */}
      <div className="h-0.5" style={{ background: `${accent}60` }} />

      <div className="p-4 flex flex-col h-full">
        {/* Category label */}
        <span
          className="text-[9px] font-bold uppercase tracking-[0.15em] mb-2"
          style={{ color: accent, opacity: 0.7, fontFamily: 'Manrope, sans-serif' }}
        >
          {category || 'Tracked'}
        </span>

        {/* Title */}
        <h3
          className="text-lg font-black text-white leading-tight mb-3"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {name}
        </h3>

        {/* Status message */}
        <div
          className="mt-auto flex items-center gap-2 pt-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Clock size={12} style={{ color: accent, opacity: 0.8 }} />
          <span
            className="text-[11px] font-semibold"
            style={{ color: '#D1D5DB', fontFamily: 'Manrope, sans-serif' }}
          >
            Available within 24 hours
          </span>
        </div>

        {/* Subtle hint line */}
        <p
          className="text-[10px] text-gray-500 mt-1.5 italic leading-relaxed"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Our pipeline will pick this up on its next run (every 4 hours).
        </p>
      </div>
    </div>
  );
}


// Single-topic row — cards stack vertically so all are fully visible without
// any horizontal scroll. Per user spec iter 61k: "show all 3 cards fully in a
// row, no need to scroll. expand the view."
function TopicRow({ term, cards, persona, tabContext, isNews, onTopicClick }) {
  return (
    <div className="mb-4" data-testid={`row-${tabContext}-${term}`}>
      {/* Per-topic header */}
      <div className="flex items-baseline justify-between mb-1.5 px-0.5">
        <h3 className="text-sm font-bold text-white truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {term}
        </h3>
        <span className="text-[10px] text-gray-500 shrink-0 ml-2">
          {cards.length} {cards.length === 1 ? 'story' : 'stories'}
        </span>
      </div>
      {/* Cards laid out in a row when the viewport is wide enough.
          Mobile (<640px): 1 col stacked. Tablet (640–1024px): 2 cols.
          Desktop (≥1024px): 3 cols side-by-side. Per user spec iter 61L:
          "show all 3 cards fully in a row, no need to scroll, expand the view." */}
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c, idx) => (
          <TrendCard
            key={c.canonical_id}
            entity={c}
            persona={persona}
            index={idx}
            isNews={!!isNews}
            tabContext={tabContext}
            onClick={isNews && onTopicClick ? () => onTopicClick({
              id: c.canonical_id, title: c.entity_name, category: c.category,
              trend_score: c.hotness_score, source_url: c.source_url, headline: c.headline,
              image_url: c.image_url,
            }) : null}
          />
        ))}
      </div>
    </div>
  );
}


function ResultsPanel({ feed, loading, onTopicClick }) {
  if (loading) {
    return (
      <div className="text-center py-8">
        <Loader2 className="inline animate-spin text-gray-500" size={20} />
      </div>
    );
  }
  if (!feed) return null;
  const totalCards = (feed.news?.length || 0) + (feed.investor?.length || 0) + (feed.reseller?.length || 0);
  if (!totalCards) {
    return (
      <div className="text-center py-6 text-white/70 text-sm font-semibold"
           style={{ fontFamily: 'Manrope, sans-serif' }}>
        No live matches yet. Save your portfolio to start tracking.
      </div>
    );
  }
  return (
    <div data-testid="myportfolio-results">
      <h2 className="text-[12px] font-black uppercase tracking-wider text-white mb-3 mt-2"
          style={{ fontFamily: 'Manrope, sans-serif' }}>Your tracked stories</h2>
      {feed.news?.length > 0 && (
        <ResultSection title="News" Icon={Newspaper} accent="#3B82F6" items={feed.news} onTopicClick={onTopicClick} kind="news" />
      )}
      {feed.investor?.length > 0 && (
        <ResultSection title="Investor" Icon={TrendingUp} accent="#10B981" items={feed.investor} onTopicClick={onTopicClick} kind="investor" />
      )}
      {feed.reseller?.length > 0 && (
        <ResultSection title="Marketplace seller" Icon={ShoppingBag} accent="#F59E0B" items={feed.reseller} onTopicClick={onTopicClick} kind="reseller" />
      )}
    </div>
  );
}


function ResultSection({ title, Icon, accent, items, onTopicClick, kind }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={12} style={{ color: accent }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>{title}</span>
        <span className="text-[10px] text-gray-500">· {items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const title = it.title || it.entity_name || it.product || 'Untitled';
          const sub = it.matched_term ? `Matches "${it.matched_term}"` : (it.headline || it.category || '');
          return (
            <button
              key={`${kind}-${i}`}
              data-testid={`result-${kind}-${i}`}
              onClick={() => kind === 'news' && onTopicClick && onTopicClick({
                id: it.id, title: it.title, category: it.category,
                trend_score: it.trend_score, source_url: it.source_url, headline: it.headline,
              })}
              className="w-full text-left rounded-xl p-3 transition-all hover:brightness-110"
              style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <div className="flex items-start gap-2">
                {it.image_url && (
                  <img src={it.image_url} alt=""
                    className="w-12 h-12 rounded-lg object-cover shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white leading-snug line-clamp-2">{title}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function PremiumGate({ onBack }) {
  return (
    <div data-testid="myportfolio-premium-gate" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <button onClick={onBack} className="absolute top-5 left-5 text-gray-400 hover:text-white">
        <ChevronLeft size={22} />
      </button>
      <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
        <Lock size={26} className="text-emerald-500" />
      </div>
      <h1 className="text-xl font-bold text-white mb-2">My Portfolio is premium</h1>
      <p className="text-sm text-gray-400 max-w-xs leading-relaxed mb-6">
        Track specific topics, brands, and companies — and get a personalised feed of matching stories.
      </p>
      <Sparkles size={20} className="text-amber-400" />
    </div>
  );
}
